// @ts-strict-ignore
import {
  mergeAttributeValueDeleteErrors,
  mergeFileUploadErrors,
} from "@dashboard/attributes/utils/data";
import {
  handleDeleteMultipleAttributeValues,
  handleUploadMultipleFiles,
} from "@dashboard/attributes/utils/handlers";
import {
  AttributeErrorFragment,
  ErrorPolicyEnum,
  MetadataErrorCode,
  MetadataErrorFragment,
  ProductChannelListingErrorFragment,
  ProductErrorCode,
  ProductErrorFragment,
  ProductErrorWithAttributesFragment,
  ProductFragment,
  UploadErrorFragment,
  useAttributeValueDeleteMutation,
  useFileUploadMutation,
  useProductChannelListingUpdateMutation,
  useProductUpdateMutation,
  useProductVariantBulkCreateMutation,
  useProductVariantBulkDeleteMutation,
  useProductVariantBulkUpdateMutation,
  useUpdateMetadataMutation,
  useUpdatePrivateMetadataMutation,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationErrors } from "@dashboard/misc";
import { ProductUpdateSubmitData } from "@dashboard/products/components/ProductUpdatePage/types";
import { getProductErrorMessage } from "@dashboard/utils/errors";
import createMetadataUpdateHandler from "@dashboard/utils/handlers/metadataUpdateHandler";
import { useState } from "react";
import { useIntl } from "react-intl";

import {
  getCreateVariantMutationError,
  getVariantUpdateMutationErrors,
  ProductVariantListError,
} from "./errors";
import {
  getBulkVariantUpdateInputs,
  getCreateVariantInput,
  getProductChannelsUpdateVariables,
  getProductUpdateVariables,
  hasProductChannelsUpdate,
} from "./utils";

export type UseProductUpdateHandlerError =
  | ProductErrorWithAttributesFragment
  | ProductErrorFragment
  | AttributeErrorFragment
  | UploadErrorFragment
  | ProductChannelListingErrorFragment
  | ProductVariantListError;

type UseProductUpdateHandler = (
  data: ProductUpdateSubmitData,
) => Promise<Array<UseProductUpdateHandlerError | MetadataErrorFragment>>;

interface UseProductUpdateHandlerConfig {
  /**
   * Toast "Saved changes" when every mutation came back clean (default).
   * The product page turns it off and toasts once itself, after the writes
   * it runs on top of this handler (secondary categories) also succeeded.
   */
  notifySuccess?: boolean;
}

/**
 * A mutation that resolved without its payload failed at transport or
 * GraphQL level (makeMutation resolves those, with `data` undefined, instead
 * of rejecting, and has already toasted the error). Reported as an error
 * entry so the save never counts as clean.
 */
export const missingPayloadError = (mutation: string): ProductErrorFragment => ({
  __typename: "ProductError",
  code: ProductErrorCode.GRAPHQL_ERROR,
  field: null,
  message: `${mutation} did not complete`,
});

const missingMetadataPayloadError = (mutation: string): MetadataErrorFragment => ({
  __typename: "MetadataError",
  code: MetadataErrorCode.GRAPHQL_ERROR,
  field: null,
  message: `${mutation} did not complete`,
});

interface UseProductUpdateHandlerOpts {
  called: boolean;
  loading: boolean;
  errors: ProductErrorWithAttributesFragment[];
  variantListErrors: ProductVariantListError[];
  channelsErrors: ProductChannelListingErrorFragment[];
}

export function useProductUpdateHandler(
  product: ProductFragment,
  { notifySuccess = true }: UseProductUpdateHandlerConfig = {},
): [UseProductUpdateHandler, UseProductUpdateHandlerOpts] {
  const intl = useIntl();
  const notify = useNotifier();
  const [variantListErrors, setVariantListErrors] = useState<ProductVariantListError[]>([]);
  const [called, setCalled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updateMetadata] = useUpdateMetadataMutation({});
  const [updatePrivateMetadata] = useUpdatePrivateMetadataMutation({});
  const [updateVariants] = useProductVariantBulkUpdateMutation();
  const [createVariants] = useProductVariantBulkCreateMutation();
  const [deleteVariants] = useProductVariantBulkDeleteMutation();
  const [uploadFile] = useFileUploadMutation();
  const [updateProduct, updateProductOpts] = useProductUpdateMutation();
  const [updateChannels, updateChannelsOpts] = useProductChannelListingUpdateMutation({
    onCompleted: data => {
      if (data.productChannelListingUpdate.errors.length) {
        data.productChannelListingUpdate.errors.forEach(error =>
          notify({
            status: "error",
            text: getProductErrorMessage(error, intl),
          }),
        );
      }
    },
  });
  const [deleteAttributeValue] = useAttributeValueDeleteMutation();
  const sendMutations = async (
    data: ProductUpdateSubmitData,
  ): Promise<UseProductUpdateHandlerError[]> => {
    let errors: UseProductUpdateHandlerError[] = [];
    const variantErrors: ProductVariantListError[] = [];
    const uploadFilesResult = await handleUploadMultipleFiles(
      data.attributesWithNewFileValue,
      variables => uploadFile({ variables }),
    );
    const deleteAttributeValuesResult = await handleDeleteMultipleAttributeValues(
      data.attributesWithNewFileValue,
      product?.attributes,
      variables => deleteAttributeValue({ variables }),
    );
    const updateProductChannelsData = getProductChannelsUpdateVariables(product, data);

    if (hasProductChannelsUpdate(updateProductChannelsData.input)) {
      const updateChannelsResult = await updateChannels({
        variables: updateProductChannelsData,
      });

      const channelsPayload = updateChannelsResult?.data?.productChannelListingUpdate;

      errors = channelsPayload
        ? [...errors, ...channelsPayload.errors]
        : [...errors, missingPayloadError("productChannelListingUpdate")];
    }

    if (data.variants.removed.length > 0) {
      const deleteVaraintsResult = await deleteVariants({
        variables: {
          ids: data.variants.removed.map(index => product.variants[index].id),
        },
      });

      const deletePayload = deleteVaraintsResult?.data?.productVariantBulkDelete;

      errors = deletePayload
        ? [...errors, ...deletePayload.errors]
        : [...errors, missingPayloadError("productVariantBulkDelete")];
    }

    const updateProductResult = await updateProduct({
      variables: getProductUpdateVariables(product, data, uploadFilesResult),
    });

    if (data.variants.added.length > 0) {
      const createVariantsResults = await createVariants({
        variables: {
          id: product.id,
          inputs: data.variants.added.map(index => ({
            ...getCreateVariantInput(
              data.variants,
              index,
              product?.productType?.variantAttributes ?? [],
            ),
          })),
        },
      });

      if (createVariantsResults?.data?.productVariantBulkCreate) {
        const createVariantsErrors = getCreateVariantMutationError(createVariantsResults);

        errors.push(...createVariantsErrors);
        variantErrors.push(...createVariantsErrors);
      } else {
        errors.push(missingPayloadError("productVariantBulkCreate"));
      }
    }

    if (data.variants.updates.length > 0) {
      const updateInputdData = getBulkVariantUpdateInputs(
        product.variants,
        data.variants,
        product?.productType?.variantAttributes ?? [],
      );

      if (updateInputdData.length) {
        const updateVariantsResults = await updateVariants({
          variables: {
            product: product.id,
            input: updateInputdData,
            errorPolicy: ErrorPolicyEnum.REJECT_FAILED_ROWS,
          },
        });

        if (updateVariantsResults?.data?.productVariantBulkUpdate) {
          const updateVariantsErrors = getVariantUpdateMutationErrors(
            updateVariantsResults,
            updateInputdData.map(data => data.id),
          );

          variantErrors.push(...updateVariantsErrors);
          errors.push(...updateVariantsErrors);
        } else {
          errors.push(missingPayloadError("productVariantBulkUpdate"));
        }
      }
    }

    const productPayload = updateProductResult?.data?.productUpdate;

    errors = [
      ...errors,
      ...mergeFileUploadErrors(uploadFilesResult),
      ...uploadFilesResult
        .filter(result => result && !result.data?.fileUpload)
        .map(() => missingPayloadError("fileUpload")),
      ...mergeAttributeValueDeleteErrors(deleteAttributeValuesResult),
      // An undefined entry is an attribute whose value was not deleted (no
      // mutation sent); only a sent mutation without its payload failed.
      ...deleteAttributeValuesResult
        .filter(result => result && !result.data?.attributeValueDelete)
        .map(() => missingPayloadError("attributeValueDelete")),
      // A missing payload is a failed save, not an empty error list: taking
      // it as clean toasted "Saved changes" and let the page write the
      // secondary categories against the old primary.
      ...(productPayload ? productPayload.errors : [missingPayloadError("productUpdate")]),
    ];
    setVariantListErrors(variantErrors);

    return errors;
  };
  const submit = async (data: ProductUpdateSubmitData) => {
    setCalled(true);
    setLoading(true);

    // Handle slug change and update redirects metadata in form data
    let modifiedData = data;

    if (data.slug && data.slug !== product.slug) {
      const oldSlug = product.slug;
      const redirectsMetadata = product.metadata?.find(item => item.key === "redirects");
      let redirects: string[] = [];

      // Parse existing redirects
      if (redirectsMetadata?.value) {
        try {
          redirects = JSON.parse(redirectsMetadata.value);

          if (!Array.isArray(redirects)) {
            redirects = [];
          }
        } catch (e) {
          redirects = [];
        }
      }

      // Add old slug to redirects if not already present
      if (oldSlug && !redirects.includes(oldSlug)) {
        redirects.push(oldSlug);
      }

      // Update the metadata in the form data
      if (redirects.length > 0) {
        const existingMetadata = data.metadata || [];
        const otherMetadata = existingMetadata.filter(item => item.key !== "redirects");

        modifiedData = {
          ...data,
          metadata: [
            ...otherMetadata,
            {
              key: "redirects",
              value: JSON.stringify(redirects),
            },
          ],
        };
      }
    }

    const errors = await createMetadataUpdateHandler(
      product,
      sendMutations,
      async variables => {
        const result = await updateMetadata({ variables });

        return result?.data?.updateMetadata
          ? result
          : {
              ...result,
              data: {
                __typename: "Mutation",
                deleteMetadata: null,
                updateMetadata: {
                  __typename: "UpdateMetadata",
                  errors: [missingMetadataPayloadError("updateMetadata")],
                  item: null,
                },
              },
            };
      },
      async variables => {
        const result = await updatePrivateMetadata({ variables });

        return result?.data?.updatePrivateMetadata
          ? result
          : {
              ...result,
              data: {
                __typename: "Mutation",
                deletePrivateMetadata: null,
                updatePrivateMetadata: {
                  __typename: "UpdatePrivateMetadata",
                  errors: [missingMetadataPayloadError("updatePrivateMetadata")],
                  item: null,
                },
              },
            };
      },
    )(modifiedData);

    setLoading(false);

    if (errors.length === 0 && notifySuccess) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });
    }

    return errors;
  };
  const errors = getMutationErrors(updateProductOpts) as ProductErrorWithAttributesFragment[];
  const channelsErrors = updateChannelsOpts?.data?.productChannelListingUpdate?.errors ?? [];

  return [
    submit,
    {
      called,
      loading,
      channelsErrors,
      errors,
      variantListErrors,
    },
  ];
}
